import type { ReportMessage } from '@hooks/useReportingThread';

export type AssistantMessage = Extract<ReportMessage, { role: 'assistant' }>;
export type ToolMessage = Extract<ReportMessage, { role: 'tool' }>;

/** One exchange: what the person asked, what the agent said, and the cards it put up. */
export interface Turn {
    id: string;
    user?: Extract<ReportMessage, { role: 'user' }>;
    assistant?: AssistantMessage;
    tools: ToolMessage[];
}

/**
 * The stream appends cards and text as separate messages in arrival
 * order, which is cards first and the answer last. Read as a page, the
 * answer belongs on top: the sentence that answers the question, then
 * the table it refers to, then the points that need the table in view.
 */
export function groupTurns(messages: ReportMessage[]): Turn[] {
    const turns: Turn[] = [];
    let current: Turn | undefined;
    for (const message of messages) {
        if (message.role === 'user') {
            current = { id: message.id, user: message, tools: [] };
            turns.push(current);
            continue;
        }
        if (!current) {
            current = { id: message.id, tools: [] };
            turns.push(current);
        }
        if (message.role === 'assistant') current.assistant = message;
        else current.tools.push(message);
    }
    return turns;
}

/** The first paragraph is the answer; the rest reads below the cards. */
export function splitLead(text: string): { lead: string; rest: string } {
    const trimmed = text.trimStart();
    const cut = trimmed.indexOf('\n\n');
    if (cut < 0) return { lead: trimmed, rest: '' };
    return { lead: trimmed.slice(0, cut), rest: trimmed.slice(cut + 2).trim() };
}
